import { lazy, Suspense, useEffect } from "react";
import { loadCreate, loadLegalDocuments, loadMarketplace, loadProfile, loadSearch, scheduleIdlePreload } from "@/lib/pagePreload";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClientProvider } from "@tanstack/react-query";
import { queryClient } from "@/lib/queryClient";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { useAgeOnboarding } from "@/hooks/useAgeOnboarding";
import { useDebugPanel } from "@/hooks/useDebugPanel";

import { SaleNotifications } from "@/components/SaleNotifications";
import { VideoReadyNotifications } from "@/components/VideoReadyNotifications";
import CookieConsentBanner from "@/components/cookies/CookieConsentBanner";

import { ThemeProvider } from "@/components/ThemeProvider";

// Critical pages - loaded immediately
import Feed from "./pages/Feed";

// Non-critical pages - lazy loaded
const Auth = lazy(() => import("./pages/Auth"));
const AuthVerify = lazy(() => import("./pages/AuthVerify"));
const Profile = lazy(loadProfile);
const Search = lazy(loadSearch);
const Create = lazy(loadCreate);
const Marketplace = lazy(loadMarketplace);
const TamaShop = lazy(() => import("./pages/TamaShop"));

const Admin = lazy(() => import("./pages/Admin"));
const ProAccountApplication = lazy(() => import("./pages/ProAccountApplication"));
const BuyCocoPoints = lazy(() => import("./pages/BuyCocoPoints"));
const Contact = lazy(() => import("./pages/Contact"));
const LegalDocument = lazy(() => import("./pages/LegalDocument"));
const LegalDocuments = lazy(loadLegalDocuments);
const NotFound = lazy(() => import("./pages/NotFound"));
const OAuthConsent = lazy(() => import("./pages/OAuthConsent"));
const Video = lazy(() => import("./pages/Video"));
/** Banc d'essai du pager 3 cases : route non listée dans la navigation. */


/**
 * Les routes protégées renvoient toujours vers l'accueil : le feed public y
 * affiche le voile d'authentification (popup de connexion / finalisation).
 */
const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const { user, loading } = useAuth();
  const { state: ageState, isLoading: ageLoading } = useAgeOnboarding();

  if (loading || (user && ageLoading)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!user || !ageState.complete) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
};



const LoadingFallback = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full" />
  </div>
);

/** Précharge le code des pages principales au repos, 4 s après le premier affichage. */
const IdlePagePreloader = () => {
  useEffect(() => scheduleIdlePreload(), []);
  return null;
};

const DebugPanel = lazy(() => import("@/components/DebugPanel"));

/** Panneau de diagnostic : chargé uniquement pour un admin qui l'a demandé. */
const AdminDebugPanelGate = () => {
  const { enabled, close } = useDebugPanel();
  if (!enabled) return null;
  return (
    <Suspense fallback={null}>
      <DebugPanel onClose={close} />
    </Suspense>
  );
};

const App = () => (
  <ThemeProvider>
    <IdlePagePreloader />
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <AuthProvider>

          <SaleNotifications />
          <VideoReadyNotifications />
          <CookieConsentBanner />
          <AdminDebugPanelGate />

          <Suspense fallback={<LoadingFallback />}>
            <Routes>
              <Route path="/auth" element={<Auth />} />
              <Route path="/auth/verifier" element={<AuthVerify />} />
              <Route path="/onboard" element={<Navigate to="/" replace />} />

              <Route path="/.lovable/oauth/consent" element={<OAuthConsent />} />
              {/* Lien de partage : page publique (feed amorcé / aperçu visiteur). */}
              <Route path="/video/:id" element={<Video />} />
              {/* Accueil public : feed visible, verrouillé par AuthGate. */}
              <Route path="/" element={<Feed />} />
              <Route path="/feed2" element={<Navigate to="/" replace />} />

            <Route
              path="/profile"
              element={
                <ProtectedRoute>
                  <Profile />
                </ProtectedRoute>
              }
            />
            <Route
              path="/profile/:userId"
              element={
                <ProtectedRoute>
                  <Profile />
                </ProtectedRoute>
              }
            />
            <Route
              path="/search"
              element={
                <ProtectedRoute>
                  <Search />
                </ProtectedRoute>
              }
            />
            <Route
              path="/create"
              element={
                <ProtectedRoute>
                  <Create />
                </ProtectedRoute>
              }
            />
            <Route
              path="/marketplace"
              element={
                <ProtectedRoute>
                  <Marketplace />
                </ProtectedRoute>
              }
            />
            <Route
              path="/tama"
              element={
                <ProtectedRoute>
                  <TamaShop />
                </ProtectedRoute>
              }
            />

            <Route
              path="/admin"
              element={
                <ProtectedRoute>
                  <Admin />
                </ProtectedRoute>
              }
            />
            <Route
              path="/pro-application"
              element={
                <ProtectedRoute>
                  <ProAccountApplication />
                </ProtectedRoute>
              }
            />
            <Route
              path="/buy-coco"
              element={
                <ProtectedRoute>
                  <BuyCocoPoints />
                </ProtectedRoute>
              }
            />
            <Route
              path="/settings"
              element={
                <ProtectedRoute>
                  <Navigate to="/profile" replace />
                </ProtectedRoute>
              }
            />
            <Route
              path="/notifications"
              element={
                <ProtectedRoute>
                  <Navigate to="/profile" replace />
                </ProtectedRoute>
              }
            />
            <Route
              path="/contact"
              element={
                <ProtectedRoute>
                  <Contact />
                </ProtectedRoute>
              }
            />
              <Route path="/legal" element={<LegalDocuments />} />
              <Route path="/legal/:slug" element={<LegalDocument />} />
            <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
          </AuthProvider>
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
  </ThemeProvider>
);


export default App;
